/**
 * Device Test Modal Component
 * Allows users to test their camera and microphone before a teleconsultation
 * Multilingual support via useLanguage hook
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Video, VideoOff, Mic, MicOff, CheckCircle, XCircle,
  AlertTriangle, Loader2, Settings, Volume2, RefreshCw
} from 'lucide-react';

const DeviceTestModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const { isDarkMode } = useTheme();
  
  // State
  const [testState, setTestState] = useState('idle'); // idle, testing, success, failed
  const [cameraStatus, setCameraStatus] = useState(null); // null, working, failed
  const [micStatus, setMicStatus] = useState(null);
  const [micLevel, setMicLevel] = useState(0);
  const [error, setError] = useState(null);
  
  // Devices
  const [devices, setDevices] = useState({ video: [], audio: [] });
  const [selectedCamera, setSelectedCamera] = useState('');
  const [selectedMic, setSelectedMic] = useState('');
  
  // Refs
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);

  // Get available devices
  const getDevices = async () => {
    try {
      // Request permissions first to get device labels
      await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
        .then(stream => stream.getTracks().forEach(t => t.stop()));
      
      const deviceList = await navigator.mediaDevices.enumerateDevices();
      setDevices({
        video: deviceList.filter(d => d.kind === 'videoinput'),
        audio: deviceList.filter(d => d.kind === 'audioinput')
      });
    } catch (err) {
      console.error('Error getting devices:', err);
      if (err.name === 'NotAllowedError') {
        setError(t('teleconsultation.permissions_denied'));
      }
    }
  };

  // Start test
  const startTest = async () => {
    setTestState('testing');
    setError(null);
    setCameraStatus(null);
    setMicStatus(null);
    
    try {
      // Check browser support
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(t('teleconsultation.browser_not_supported'));
      }
      
      const constraints = {
        video: selectedCamera ? { deviceId: { exact: selectedCamera } } : true,
        audio: selectedMic ? { deviceId: { exact: selectedMic } } : true
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      // Test Camera
      const videoTracks = stream.getVideoTracks();
      const cameraOk = videoTracks.length > 0 && videoTracks[0].readyState === 'live';
      setCameraStatus(cameraOk ? 'working' : 'failed');
      if (cameraOk && videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      
      // Test Microphone
      const audioTracks = stream.getAudioTracks();
      const micOk = audioTracks.length > 0 && audioTracks[0].readyState === 'live';
      setMicStatus(micOk ? 'working' : 'failed');
      if (micOk) {
        startMicLevelMonitoring(stream);
      }
      
      // Determine overall status
      setTimeout(() => {
        if (cameraOk && micOk) {
          setTestState('success');
        } else {
          setTestState('failed');
        }
      }, 1500);
      
    } catch (err) {
      console.error('Test error:', err);
      setTestState('failed');
      
      if (err.name === 'NotAllowedError') {
        setError(t('teleconsultation.permissions_denied'));
      } else if (err.name === 'NotFoundError') {
        setError(t('teleconsultation.no_device_found'));
      } else {
        setError(err.message || t('teleconsultation.device_error'));
      }
    }
  };

  // Monitor microphone level
  const startMicLevelMonitoring = (stream) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      const analyser = audioContext.createAnalyser();
      analyserRef.current = analyser;
      analyser.fftSize = 256;
      
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      const checkLevel = () => {
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        const normalizedLevel = Math.min(100, (average / 128) * 100);
        setMicLevel(normalizedLevel);
        animationRef.current = requestAnimationFrame(checkLevel);
      };
      
      checkLevel();
    } catch (err) {
      console.error('Audio monitoring error:', err);
    }
  };

  // Stop test
  const stopTest = useCallback(() => {
    // Stop all tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    // Stop audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    // Cancel animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    
    // Clear video
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setMicLevel(0);
  }, []);

  // Handle close
  const handleClose = useCallback(() => {
    stopTest();
    setTestState('idle');
    setCameraStatus(null);
    setMicStatus(null);
    setError(null);
    onClose();
  }, [stopTest, onClose]);

  // Handle success
  const handleContinue = () => {
    stopTest();
    onSuccess?.();
    handleClose();
  };

  // Load devices on mount
  useEffect(() => {
    if (isOpen) {
      getDevices();
    }
    return () => {
      stopTest();
    };
  }, [isOpen, stopTest]);

  // Update test state based on camera/mic status
  useEffect(() => {
    if (testState === 'testing' && cameraStatus && micStatus) {
      if (cameraStatus === 'working' && micStatus === 'working') {
        setTestState('success');
      } else {
        setTestState('failed');
      }
    }
  }, [cameraStatus, micStatus, testState]);

  const StatusIcon = ({ status }) => {
    if (status === 'working') {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    } else if (status === 'failed') {
      return <XCircle className="w-5 h-5 text-red-500" />;
    }
    return <Loader2 className="w-5 h-5 animate-spin text-blue-500" />;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className={`max-w-lg ${isDarkMode ? 'bg-slate-900 border-slate-700' : ''}`}>
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
            <Settings className="w-5 h-5 text-blue-500" />
            {t('teleconsultation.test_title')}
          </DialogTitle>
          <DialogDescription className={isDarkMode ? 'text-slate-400' : ''}>
            {t('teleconsultation.test_description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Video Preview */}
          <div className="relative aspect-video bg-slate-900 rounded-xl overflow-hidden">
            {testState === 'idle' ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500">
                <Video className="w-16 h-16 mb-2 opacity-50" />
                <p className="text-sm">{t('teleconsultation.start_test')}</p>
              </div>
            ) : (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className={`w-full h-full object-cover ${cameraStatus === 'failed' ? 'hidden' : ''}`}
                />
                {cameraStatus === 'failed' && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-red-400">
                    <VideoOff className="w-16 h-16 mb-2" />
                    <p className="text-sm">{t('teleconsultation.camera_not_working')}</p>
                  </div>
                )}
              </>
            )}
            
            {/* Status Badge */}
            {testState !== 'idle' && (
              <Badge
                className={`absolute top-3 right-3 ${
                  testState === 'success' ? 'bg-green-500' :
                  testState === 'failed' ? 'bg-red-500' : 'bg-blue-500'
                }`}
              >
                {testState === 'testing' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                {testState === 'success' && <CheckCircle className="w-3 h-3 mr-1" />}
                {testState === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
                {testState === 'testing' ? 'Testing...' : testState === 'success' ? 'OK' : 'Error'}
              </Badge>
            )}
          </div>

          {/* Device Selection */}
          {devices.video.length > 0 && (
            <div className="space-y-3">
              <div>
                <label className={`text-sm font-medium block mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  {t('teleconsultation.select_camera')}
                </label>
                <select
                  value={selectedCamera}
                  onChange={(e) => setSelectedCamera(e.target.value)}
                  className={`w-full p-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-slate-800 border-slate-600 text-white' 
                      : 'bg-white border-slate-300'
                  }`}
                  disabled={testState === 'testing'}
                >
                  <option value="">{t('teleconsultation.camera')} - Auto</option>
                  {devices.video.map(device => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className={`text-sm font-medium block mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  {t('teleconsultation.select_microphone')}
                </label>
                <select
                  value={selectedMic}
                  onChange={(e) => setSelectedMic(e.target.value)}
                  className={`w-full p-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-slate-800 border-slate-600 text-white' 
                      : 'bg-white border-slate-300'
                  }`}
                  disabled={testState === 'testing'}
                >
                  <option value="">{t('teleconsultation.microphone')} - Auto</option>
                  {devices.audio.map(device => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Status Cards */}
          <div className="grid grid-cols-2 gap-4">
            {/* Camera Status */}
            <div className={`p-4 rounded-xl border ${
              isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  {t('teleconsultation.test_camera')}
                </span>
                {testState !== 'idle' && <StatusIcon status={cameraStatus} />}
              </div>
              <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                {cameraStatus === 'working' 
                  ? t('teleconsultation.camera_working')
                  : cameraStatus === 'failed'
                  ? t('teleconsultation.camera_not_working')
                  : testState === 'testing' ? 'Testing...' : '-'}
              </p>
            </div>

            {/* Microphone Status */}
            <div className={`p-4 rounded-xl border ${
              isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  {t('teleconsultation.test_microphone')}
                </span>
                {testState !== 'idle' && <StatusIcon status={micStatus} />}
              </div>
              <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                {micStatus === 'working' 
                  ? t('teleconsultation.mic_working')
                  : micStatus === 'failed'
                  ? t('teleconsultation.mic_not_working')
                  : testState === 'testing' ? 'Testing...' : '-'}
              </p>
            </div>
          </div>

          {/* Microphone Level */}
          {testState !== 'idle' && micStatus === 'working' && (
            <div className={`p-4 rounded-xl border ${
              isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <Volume2 className={`w-4 h-4 ${micLevel > 10 ? 'text-green-500' : 'text-slate-400'}`} />
                <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  {t('teleconsultation.mic_level')}
                </span>
              </div>
              <Progress value={micLevel} className="h-2" />
              <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                {t('teleconsultation.speak_now')}
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {testState === 'success' && (
            <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-700 dark:text-green-300">
                {t('teleconsultation.test_success')}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          {testState === 'idle' ? (
            <>
              <Button variant="outline" onClick={handleClose}>
                {t('cancel')}
              </Button>
              <Button onClick={startTest} className="bg-blue-600 hover:bg-blue-700">
                <Video className="w-4 h-4 mr-2" />
                {t('teleconsultation.start_test')}
              </Button>
            </>
          ) : testState === 'testing' ? (
            <Button variant="outline" onClick={stopTest}>
              {t('teleconsultation.stop_test')}
            </Button>
          ) : testState === 'success' ? (
            <>
              <Button variant="outline" onClick={() => { stopTest(); setTestState('idle'); }}>
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('teleconsultation.start_test')}
              </Button>
              <Button onClick={handleContinue} className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="w-4 h-4 mr-2" />
                {t('confirm')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose}>
                {t('cancel')}
              </Button>
              <Button onClick={() => { stopTest(); setTestState('idle'); startTest(); }}>
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('teleconsultation.start_test')}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeviceTestModal;
